//Layouts
import { Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import { ChunkBoundary, lazyRetryImport } from "../components/ui/ChunkBoundary";
import MainLayout from "../layouts/mainLayout";
import LoginLayout from "../layouts/loginLayout";
import ControlTowerLayout from "../layouts/controlTowerLayout";
import { Navigate } from "react-router-dom";
import { roleRoutes } from "utils/roles";
import { TICKETS_ENABLED } from "../config/featureFlags";
import { useAuthToken } from "../providers/authProviders";
import { buildHelpdeskIncidentsRoutes } from "./helpdeskIncidentsRoute";

//Pages
import LoginView from '../pages/login'
import ForgotPassword from '../pages/login/recoveryPassword'
import SetNewPassword from '../pages/login/setNewPassword'
import AcceptInvite from '../pages/login/acceptInvite'
import EnterRecoveryCode from '../pages/login/enterRecoveryCode'
import DeliveredView from '../pages/delivered'
import Error from '../pages/error'
import AllOrders from '../pages/allOrders'
import Resume from '../pages/resume'
import Integrations from '../pages/integrations'
import JumpsellerCallback from '../views/clientView/integrations/callbacks/JumpsellerCallback'
import TiendaNubeCallback from '../views/clientView/integrations/callbacks/TiendaNubeCallback'
import WixCallback from '../views/clientView/integrations/callbacks/WixCallback'
import FilteredOrders from '../views/clientView/integrations/FilteredOrders'
import ConnectStoreView from '../views/clientView/integrations/ConnectStoreView'
import Faqs from '../pages/faqs'
import AdminFaqs from '../pages/adminFaqs'
import Informs from '../pages/informs'
import LockUsers from '../pages/bloquedUsers'
import MyAcountClient from '../pages/myAcountClient'
import ClientInform from '../pages/clientInform'
import Fleet from '../pages/fleet'
import Team from '../pages/team'
import Routes from '../pages/routes'


// AdminRoute (el Rutificador) va en chunk lazy — wrapper más abajo.
import AdminWithdrawals from "../pages/adminWithdrawals";
// LiveRoutes/LiveRoutesNueva van en chunk lazy (wrappers más abajo, mismo
// patrón que PlatformArea): vistas grandes de monitoreo que no todo usuario
// abre — nadie debería pagarlas en el bundle inicial.
import LiveTracking from "../pages/liveTracking";
import AdminPickupViewPage from "pages/adminPickups";
import AdminRecolecciones from "../pages/adminRecolecciones";
import AdminPendingAddresses from "../pages/adminPendingAddresses";
import RecepcionBodega from "../pages/recepcionBodega";
import AdminContractsViewPage from "pages/adminContracts";
import NewDashboard from "views/adminView/newDashboard/NewDashboard";
import IncidentsView from "views/adminView/incidents";
import TorreControl from "views/adminView/torreControl";
import FleetMap from "../pages/FleetMap";
import AdminNotifications from "views/adminView/notifications";
import AdminBillingPage from "../pages/adminBilling";
import AdminCobroView from "views/adminView/cobro/CobroView";
import DriverPaymentsSummary from "views/adminView/cobro/DriverPaymentsSummary";
import PagosView from "views/clientView/pagos/PagosView";
import HelpdeskPage from "../pages/helpdesk";
import AdminContacts from "../views/adminView/helpdesk/contacts/AdminContacts";
import HelpdeskSettings from "../views/adminView/helpdesk/HelpdeskSettings";
import QuickRepliesSettings from "../views/adminView/helpdesk/QuickRepliesSettings";
import HelpdeskReports from "../views/adminView/helpdesk/HelpdeskReports";
import AiSettings from "../views/adminView/helpdesk/AiSettings";
import InternalAssistantPage from "../views/adminView/helpdesk/InternalAssistantPage";
import AdminSettingsPage from "../pages/adminSettings";
import MercadoLibreOperatorCallback from "../views/adminView/settings/callbacks/MercadoLibreOperatorCallback";
import DeliveryQualityView from "views/adminView/deliveryQuality/DeliveryQualityView";
import DeliveryQualityNext from "views/adminView/deliveryQuality/DeliveryQualityNext";
import IaConfigView from "views/adminView/iaConfig/IaConfigView";
import AlmacenamientoView from "views/adminView/almacenamiento/AlmacenamientoView";
import RequireSystemSuperadmin from "components/RequireSystemSuperadmin/requireSystemSuperadmin";
import RequireTenantModule from "components/RequireTenantModule/RequireTenantModule";
import { CLIENT_MANAGER_ROLE_ID } from "../config/menuConfig";
import UsersModule from "views/adminView/usersModule/UsersModule";
import DeliveryAnalytics from "views/adminView/deliveryAnalytics/DeliveryAnalytics";
import ForecastingView from "views/adminView/forecasting/ForecastingView";
// El módulo GPS se separó en 4 vistas (Monitoreo / Dispositivos / Geocercas
// / Alarmas). El antiguo `GpsTrackingView` monolítico fue removido — la ruta
// legacy `/admin/gps-tracking` redirige a `/admin/gps-monitoreo` para no
// romper bookmarks.
import {
  GpsMonitoreo,
  GpsDispositivos,
  GpsGeocercas,
  GpsAlarmas,
} from "views/adminView/gpsTracking";
import CompanySettings from "../views/clientView/companySettings";
import CommerceIndicators from "../views/clientView/indicadores";
import ControlTowerIA from "views/controlTower";
import {
  // HoldingDashboard removido del UI por ahora — el archivo se mantiene en
  // src/views/holdingView/HoldingDashboard.jsx para reactivar más adelante.
  HoldingCompaniesList,
  HoldingCompanyForm,
  HoldingPackages,
  HoldingPickups,
  HoldingInform,
} from "views/holdingView";

// Mientras Dashboard esté deshabilitado, /holding redirige a /holding/packages.
const HoldingIndexRedirect = () => <Navigate to="/holding/packages" replace />;

// El módulo GPS legacy (/admin/gps-tracking) redirige a la nueva
// /admin/gps-monitoreo para no romper bookmarks viejos.
const GpsTrackingLegacyRedirect = () => <Navigate to="/admin/gps-monitoreo" replace />;

// "Salud IA" dejó de ser vista propia: es el primer tab del panel de IA.
const RedirigirASaludIa = () => <Navigate to="/admin/ia-config?tab=salud" replace />;

// "Usuarios nuevos" se consolidó dentro de "Usuarios" (filtro "Por aceptar").
const UsersNewRedirect = () => <Navigate to="/admin/users" replace />;

// "Administrar flota" y "Rutas optimizadas" están rotas en el backend
// (GET /fleet → 502/timeout; el optimizer depende de ese servicio). Se quitaron
// del menú y se redirige cualquier acceso directo a Pedidos para no mostrar la
// vista rota. Reactivar cuando el backend rehaga el servicio de flota.
const BrokenFleetRedirect = () => <Navigate to="/admin" replace />;

// Prospectos vivía en el hub Empresas (tab Pipeline, retirado del subnav el
// 2026-08-20). La ruta sobrevive como redirect para bookmarks/links viejos;
// la vista AdminProspects (pages/adminProspects) queda sin enrutar como
// referencia.
const ProspectsRedirect = () => <Navigate to="/admin/contracts" replace />;

// El índice de /admin/* redirige al landing del rol. Pedidos dejó de ser la
// ruta base y pasó a /admin/pedidos; "/admin" se mantiene como home (login,
// "Inicio", "volver") apuntando al dashboard.
// El superadministrador del sistema es la excepción: el Dashboard no está en su
// menú (ver RUTAS_OCULTAS_SYSTEM_SUPERADMIN en menuConfig), así que aterriza en
// Pedidos, la única vista de operación que conserva.
// Se lee el rol del token (useAuthToken, síncrono) y no del contexto de auth:
// este componente ya renderiza dentro de RequireAdmin, que resuelve igual.
const AdminIndexRedirect = () => {
  const { role } = useAuthToken();
  const destino = role === roleRoutes.systemSuperadmin ? "/admin/pedidos" : "/admin/dashboard";
  return <Navigate to={destino} replace />;
};

// Redirect de compatibilidad: el "Dashboard Nuevo" pasó a ser el Dashboard
// principal en /admin/dashboard. Mantenemos /admin/dashboard-nuevo apuntando a
// la nueva ruta para no romper bookmarks/links viejos.
const DashboardNuevoRedirect = () => <Navigate to="/admin/dashboard" replace />;

// El antiguo "Dashboard (Old)" (analítica de performance) se unificó dentro de
// la Torre de control como su pestaña "Analítica". /admin/dashboard-old queda
// como redirect para no romper bookmarks/links viejos.
const DashboardOldRedirect = () => <Navigate to="/admin/torre-control" replace />;

// Área "Super Admin" (/plataforma/*): el portal de plataforma portado del
// mockup mu-portal. Va en su propio chunk con `lazy` porque son ~17k líneas que
// solo carga el systemSuperadmin; nadie más debería pagarlas en el bundle
// inicial. El guard de rol vive dentro de PlatformArea.
const PlatformArea = lazy(() => import("../superadmin/PlatformArea"));
const SuperAdminPlataforma = () => {
  const { t } = useTranslation();
  return (
    <Suspense
      fallback={
        <div
          className="d-flex align-items-center justify-content-center w-100"
          style={{ minHeight: "50vh", fontSize: "0.875rem", color: "var(--text-secondary)" }}
        >
          {t("Cargando plataforma…")}
        </div>
      }
    >
      <PlatformArea />
    </Suspense>
  );
};

// "Inteligencia artificial" (/admin/ia-config) — todo lo de IA en un panel:
// Salud del pipeline / General / Helpdesk / Calidad. Restringido al superadmin
// del sistema con RequireSystemSuperadmin, para que admin/superadmin logístico/
// support/supervisor —admitidos por RequireAdmin— no lleguen por URL directa.
// Se reenvían props (update/resetUpdate) del layout.
const IaConfigGuarded = (props) => (
  <RequireSystemSuperadmin>
    <IaConfigView {...props} />
  </RequireSystemSuperadmin>
);

// "Almacenamiento" (/admin/almacenamiento) — el object store, que salió del
// panel de IA porque no tiene nada que ver. Mismo guard.
const AlmacenamientoGuarded = (props) => (
  <RequireSystemSuperadmin>
    <AlmacenamientoView {...props} />
  </RequireSystemSuperadmin>
);

// Fallback compartido de los wrappers lazy de esta zona.
const lazyViewFallback = (label) => (
  <div
    className="d-flex align-items-center justify-content-center w-100"
    style={{ minHeight: "50vh", fontSize: "0.875rem", color: "var(--text-secondary)" }}
  >
    {label}
  </div>
);

// "Rutas en vivo" (/admin/live-routes) — chunk lazy propio: la vista + sus
// componentes compartidos de Actividad solo se descargan al entrar. Con
// ChunkBoundary + retry para que un blip de red (o un deploy que cambió los
// hashes) no escale al ErrorBoundary global.
const LiveRoutesView = lazy(lazyRetryImport(() => import("../pages/liveRoutes")));
const LiveRoutes = (props) => {
  const { t } = useTranslation();
  return (
    <ChunkBoundary label={t("Rutas en vivo")}>
      <Suspense fallback={lazyViewFallback(t("Cargando rutas en vivo…"))}>
        <LiveRoutesView {...props} />
      </Suspense>
    </ChunkBoundary>
  );
};

// "Tickets" (/admin/tickets) — tableros kanban del courier respaldados por el
// microservicio nuevo (microservice-tickets). Lazy: dnd-kit + el board completo
// no deben pagarse en el bundle inicial. La vista vieja de incidencias sigue
// viva en /admin/incidents (apelaciones + brazos por departamento) hasta migrar.
const TicketsViewLazy = lazy(lazyRetryImport(() => import("../views/adminView/tickets")));
const TicketsGuarded = (props) => {
  const { t } = useTranslation();
  return (
    <ChunkBoundary label={t("Tickets")}>
      <Suspense fallback={lazyViewFallback(t("Cargando tickets…"))}>
        <TicketsViewLazy {...props} />
      </Suspense>
    </ChunkBoundary>
  );
};


// "Vista en vivo (Nueva)" (/admin/live-routes-nueva) — rediseño en evaluación
// de /admin/live-routes. Se restringe al superadmin del sistema para probarlo
// contra datos reales sin exponerlo a la operación ni tocar la vista actual.
// Lazy además de guarded: su mapa (CommonOpsMap) no debe pagarlo nadie más.
const LiveRoutesNuevaView = lazy(lazyRetryImport(() => import("../pages/liveRoutesNueva")));
const LiveRoutesNuevaGuarded = (props) => {
  const { t } = useTranslation();
  return (
    <RequireSystemSuperadmin>
      <ChunkBoundary label={t("Vista en vivo (Nueva)")}>
        <Suspense fallback={lazyViewFallback(t("Cargando vista en vivo…"))}>
          <LiveRoutesNuevaView {...props} />
        </Suspense>
      </ChunkBoundary>
    </RequireSystemSuperadmin>
  );
};

// "Rutificador" — sectorización manual del pool pre-ruta sobre el mapa. Se
// habilita empresa por empresa (módulo `rutificador`, que el superadmin del
// sistema enciende desde la plataforma): el operador lo abre en
// /admin/adminRoute y el comercio en /home/rutificador, ambos con la MISMA vista.
// El superadmin del sistema entra siempre a la del operador. Todavía no crea
// rutas en el backend (arma y guarda solo en la sesión), por eso el menú lo
// marca "Beta". Lazy además de guarded: son ~2.700 líneas más Leaflet que nadie
// más debería pagar en el bundle inicial.
const AdminRouteView = lazy(lazyRetryImport(() => import("../pages/AdminRoute")));
const RutificadorLazy = (props) => {
  const { t } = useTranslation();
  return (
    <ChunkBoundary label={t("Rutificador")}>
      <Suspense fallback={lazyViewFallback(t("Cargando rutificador…"))}>
        <AdminRouteView {...props} />
      </Suspense>
    </ChunkBoundary>
  );
};

const RUTIFICADOR_OPERATOR_ROLES = [roleRoutes.admin, roleRoutes.superadmin];
const RUTIFICADOR_COMMERCE_ROLES = [roleRoutes.client, CLIENT_MANAGER_ROLE_ID];

// Portal de operación: admin y superadmin logístico con el módulo habilitado
// para su operador, o el superadmin del sistema. Soporte y supervisor (que
// RequireAdmin sí deja entrar a /admin/*) vuelven a /admin.
const AdminRouteGuarded = (props) => {
  const { t } = useTranslation();
  return (
    <RequireTenantModule
      moduleKey="rutificador"
      tenantType="logistic"
      allowSystemSuperadmin
      allowedRoles={RUTIFICADOR_OPERATOR_ROLES}
      fallbackPath="/admin"
      loadingFallback={lazyViewFallback(t("Cargando rutificador…"))}
    >
      <RutificadorLazy {...props} />
    </RequireTenantModule>
  );
};

// Portal del comercio: client y clientManager con el módulo habilitado para su
// comercio. /home/* no tiene guard de rol propio, así que este es el único filtro.
const CommerceRutificadorGuarded = (props) => {
  const { t } = useTranslation();
  return (
    <RequireTenantModule
      moduleKey="rutificador"
      tenantType="company"
      allowedRoles={RUTIFICADOR_COMMERCE_ROLES}
      fallbackPath="/home"
      loadingFallback={lazyViewFallback(t("Cargando rutificador…"))}
    >
      <RutificadorLazy {...props} />
    </RequireTenantModule>
  );
};

// /admin/helpdesk/settings: canales de la mesa de ayuda (HelpdeskSettings →
// PortalChannelToggle) con el atajo a /admin/settings → Integraciones → Canales
// de comunicación, que es donde se vinculan los números. Acceso directo y
// acotado desde el rail del helpdesk (ítem "Canales").


//Routes

const routes = [
  {
    path: "/",
    element: LoginLayout,
    exact: true,
    children: [
      {
        path: "/",
        exact: true,
        element: LoginView,
      },
    ],
  },
  {
    path: "/home/*",
    element: MainLayout,
    exact: false,
    children: [
      {
        path: "/",
        exact: true,
        element: AllOrders
      },
      {
        path: "/delivered-list",
        exact: true,
        element: DeliveredView
      },
      {
        path: "/integrations",
        exact: true,
        element: Integrations
      },
      {
        path: "/integrations/filtered",
        exact: true,
        element: FilteredOrders
      },
      {
        path: "/integrations/connect",
        exact: true,
        element: ConnectStoreView
      },
      {
        path: "/integrations/jumpseller/callback",
        exact: true,
        element: JumpsellerCallback
      },
      {
        path: "/integrations/tiendanube/callback",
        exact: true,
        element: TiendaNubeCallback
      },
      {
        // Destino del `frontendRedirectUri` que se firma en el state de Wix.
        // Sin esta ruta el comercio termina la instalación y cae en el 404 del
        // SPA, con la tienda YA vinculada: parece que falló y no falló nada.
        path: "/integrations/wix/callback",
        exact: true,
        element: WixCallback
      },
      {
        path: "/faqs",
        exact: true,
        element: Faqs
      },
      {
        path: "/my-account",
        exact: true,
        element: MyAcountClient
      },
      {
        path: "/configuracion-empresa",
        exact: true,
        element: CompanySettings
      },
      {
        path: "/indicadores",
        exact: true,
        element: CommerceIndicators
      },
      {
        // Misma bandeja que ve el operador, acotada por el backend a los
        // pedidos del comercio. El comercio es quien mejor conoce la
        // direccion que cargo, asi que tiene que poder resolverla el mismo.
        path: "/direcciones-por-resolver",
        exact: true,
        element: AdminPendingAddresses
      },
      {
        // El Rutificador del comercio: la misma vista que /admin/adminRoute,
        // con su propio guard de rol y de módulo (ver CommerceRutificadorGuarded).
        path: "/rutificador",
        exact: true,
        element: CommerceRutificadorGuarded
      },
      {
        path: "/my-report",
        exact: true,
        element: ClientInform
      },
      {
        path: "/usuarios",
        exact: true,
        element: Team
      },
      {
        // Sección "Pagos" del comercio: listado + detalle de notas de cobro.
        // Rutas anidadas (índice = lista, :id = detalle), igual que /admin/cobro/*.
        path: "/pagos/*",
        exact: false,
        element: PagosView
      },
      {
        path: "/fleet",
        exact: true,
        element: Fleet
      },
      {
        path: "/live-routes",
        exact: true,
        element: LiveRoutes
      },
      {
        path: "/live-tracking",
        exact: true,
        element: LiveTracking
      },
      {
        path: "/fleet-map",
        exact: true,
        element: FleetMap
      },
      {
        path: "*",
        exact: true,
        element: Error
      }
    ]
  },
  {
    // Super Admin — portal de plataforma (tenants, planes, billing, salud).
    // Integrado en MainLayout: un solo sidebar para todo el portal; los ítems
    // de plataforma van en menuConfig marcados "MU" (maqueta en saneamiento).
    // El acceso lo restringe RequireSystemSuperadmin dentro del área.
    path: "/plataforma/*",
    element: MainLayout,
    exact: false,
    adminOnly: true,
    children: [
      {
        path: "/*",
        exact: false,
        element: SuperAdminPlataforma,
      },
    ],
  },
  {
    path: "/admin/*",
    element: MainLayout,
    exact: false,
    adminOnly: true,
    children: [
      {
        path: "/",
        exact: true,
        element: AdminIndexRedirect
      },
      {
        path: "/pedidos",
        exact: true,
        element: Resume
      },
      {
        path: "/my-account",
        exact: true,
        element: MyAcountClient
      },
      {
        path: "/inform",
        exact: true,
        element: Informs
      },
      {
        path: "/live-routes",
        exact: true,
        element: LiveRoutes
      },
      {
        // Rediseño en evaluación, restringido al superadmin del sistema.
        path: "/live-routes-nueva",
        exact: true,
        element: LiveRoutesNuevaGuarded
      },
      {
        path: "/live-tracking",
        exact: true,
        element: LiveTracking
      },
      {
        // Torre de control — panorama operativo en vivo (KPIs, mapa,
        // repartidores, alertas). Compone dashboardService + useActividadData
        // + socket de posiciones; no usa endpoints nuevos.
        path: "/torre-control",
        exact: true,
        element: TorreControl
      },
      {
        path: "/users",
        exact: true,
        element: UsersModule
      },
      {
        path: "/users-new",
        exact: true,
        element: UsersNewRedirect
      },
      {
        path: "/lock",
        exact: true,
        element: LockUsers
      },
      {
        path: "/optimized-routes/:routeRequestId",
        exact: true,
        element: BrokenFleetRedirect
      },
      {
        path: "/optimized-routes",
        exact: true,
        element: BrokenFleetRedirect
      },
      {
        path: "/adminFleet",
        exact: true,
        element: BrokenFleetRedirect
      },
      {
        path: "/adminFleet/map",
        exact: true,
        element: FleetMap
      },
      {
        path: "/adminRoute",
        exact: true,
        element: AdminRouteGuarded
      },
      {
        path: "/adminWithdrawals",
        exact: true,
        element: AdminWithdrawals
      },
      {
        path: "/routes",
        exact: true,
        element: Routes
      },
      {
        path: "/faqs",
        exact: true,
        element: AdminFaqs
      },
      {
        path: "/pickupsView",
        exact: true,
        element: AdminPickupViewPage
      },
      {
        path: "/recolecciones",
        exact: true,
        element: AdminRecolecciones
      },
      {
        // Direcciones que el geocoder no pudo ubicar con precisión de calle
        // y número. Sin coordenada el paquete queda fuera de la ruta, así que
        // esto es trabajo del día y no una pantalla de configuración.
        path: "/direcciones-por-resolver",
        exact: true,
        element: AdminPendingAddresses
      },
      {
        path: "/recepcion-bodega",
        exact: true,
        element: RecepcionBodega
      },
      {
        path: "/contracts",
        exact: true,
        element: AdminContractsViewPage
      },
      {
        path: "/dashboard",
        exact: true,
        element: NewDashboard
      },
      {
        path: "/dashboard-old",
        exact: true,
        element: DashboardOldRedirect
      },
      {
        path: "/dashboard-nuevo",
        exact: true,
        element: DashboardNuevoRedirect
      },
      {
        path: "/incidents",
        exact: true,
        element: IncidentsView
      },
      // Detrás de TICKETS_ENABLED: con el flag apagado la ruta ni se registra,
      // así que /admin/tickets cae en el 404 del layout en vez de montar un
      // tablero que fuera de la LAN no puede cargar. Ocultar solo el ítem del
      // sidebar no alcanzaba: la URL seguía siendo alcanzable a mano.
      ...(TICKETS_ENABLED
        ? [{
            path: "/tickets",
            exact: true,
            element: TicketsGuarded
          }]
        : []),
      {
        path: "/notifications",
        exact: true,
        element: AdminNotifications
      },
      {
        path: "/billing/*",
        exact: false,
        element: AdminBillingPage
      },
      {
        // Notas de cobro a comercios (operador). Vista con rutas anidadas;
        // /cobro/driver-payments redirige al módulo propio de abajo.
        path: "/cobro/*",
        exact: false,
        element: AdminCobroView
      },
      {
        // Pago a repartidores: módulo propio desde ago-2026 (antes pestaña de
        // "Facturación y Cobro"; son dos negocios distintos).
        path: "/pago-repartidores",
        exact: true,
        element: DriverPaymentsSummary
      },
      {
        path: "/helpdesk",
        exact: true,
        element: HelpdeskPage
      },
      // "Atención e incidencias": detrás de HELPDESK_INCIDENTS_ENABLED. Con el
      // flag apagado no se registra (ver helpdeskIncidentsRoute.jsx).
      ...buildHelpdeskIncidentsRoutes(lazyViewFallback),
      {
        path: "/helpdesk/settings",
        exact: true,
        element: HelpdeskSettings
      },
      {
        path: "/helpdesk/contacts",
        exact: true,
        element: AdminContacts
      },
      {
        // Respuestas rápidas — catálogo de plantillas que los agentes insertan
        // en el chat por atajo. Misma puerta que el resto del helpdesk
        // (adminOnly del padre /admin/*); dentro, la vista restringe la edición
        // a supervisor/admin con el mismo criterio que ConversationDetail.
        path: "/helpdesk/quick-replies",
        exact: true,
        element: QuickRepliesSettings
      },
      {
        // Reportes de la mesa de ayuda (KPIs + series por día/canal/agente/
        // carpeta/hora). Misma puerta que el resto del helpdesk (adminOnly del
        // padre /admin/*); el ítem "Reportes" del rail navega acá.
        path: "/helpdesk/reports",
        exact: true,
        element: HelpdeskReports
      },
      {
        // Asistente IA (BYO): configuración por tenant del bot propio de la
        // mesa de ayuda. Misma puerta que el resto del helpdesk (adminOnly
        // del padre /admin/*); el ítem "Asistente IA" del rail navega acá.
        path: "/helpdesk/ai",
        exact: true,
        element: AiSettings
      },
      {
        // Canal interno (asistente de gerencia): hilo privado del usuario
        // logueado con el agente IA de audiencia `internal`. Misma puerta que el
        // resto del helpdesk (adminOnly del padre /admin/*); el ítem "Asistente
        // interno" del rail navega acá.
        path: "/helpdesk/interno",
        exact: true,
        element: InternalAssistantPage
      },
      {
        // Análisis de entregas (heat map analítico: concentración, Pareto,
        // ranking). Única vista de mapa analítico: /admin/delivery-map
        // (CommuneMap fullscreen) se eliminó en F2f del plan de expansión —
        // duplicaba esta vista y solo sabía de la Región Metropolitana.
        path: "/delivery-analytics",
        exact: true,
        element: DeliveryAnalytics
      },
      {
        path: "/settings",
        exact: true,
        element: AdminSettingsPage
      },
      {
        // Callback del OAuth de MercadoLibre para el operador. Bajo /admin/* para
        // que RequireAdmin admita al operador y el ámbito sea el correcto.
        path: "/settings/mercadolibre/callback",
        exact: true,
        element: MercadoLibreOperatorCallback
      },
      {
        path: "/delivery-quality",
        exact: true,
        element: DeliveryQualityNext
      },
      {
        path: "/delivery-quality-old",
        exact: true,
        element: DeliveryQualityView
      },
      {
        // "Salud IA" dejó de ser una vista propia: su monitor es ahora el primer
        // tab del panel de IA. La ruta sobrevive como redirección para no romper
        // enlaces guardados. Los tabs analíticos que la acompañaban siguen
        // intactos en /admin/delivery-quality-old, que es de donde salieron.
        path: "/ai-health",
        exact: true,
        element: RedirigirASaludIa
      },
      {
        // Inteligencia artificial — Salud / General / Helpdesk / Calidad.
        // Restringida al superadmin del sistema (ver IaConfigGuarded).
        path: "/ia-config",
        exact: true,
        element: IaConfigGuarded
      },
      {
        // Almacenamiento (object store). Salió del panel de IA: no es IA.
        path: "/almacenamiento",
        exact: true,
        element: AlmacenamientoGuarded
      },
      {
        path: "/prospects",
        exact: true,
        element: ProspectsRedirect
      },
      {
        path: "/forecasting",
        exact: true,
        element: ForecastingView
      },
      {
        // Alias legacy → redirige al monitor en vivo (mismo módulo, renombrado).
        path: "/gps-tracking",
        exact: true,
        element: GpsTrackingLegacyRedirect
      },
      {
        path: "/gps-monitoreo",
        exact: true,
        element: GpsMonitoreo
      },
      {
        path: "/gps-dispositivos",
        exact: true,
        element: GpsDispositivos
      },
      {
        path: "/gps-geocercas",
        exact: true,
        element: GpsGeocercas
      },
      {
        path: "/gps-alarmas",
        exact: true,
        element: GpsAlarmas
      },
      {
        // Catch-all del área admin: sin esto una ruta /admin/* inexistente
        // (p.ej. la eliminada /admin/users-old) dejaba el panel principal
        // VACÍO — ningún hijo matcheaba y el 404 general nunca aplicaba.
        // Mismo patrón que el catch-all del área cliente.
        path: "*",
        exact: true,
        element: Error
      },
    ]
  },
  {
    path: "/holding/*",
    element: MainLayout,
    exact: false,
    holdingOnly: true,
    children: [
      {
        path: "/",
        exact: true,
        element: HoldingIndexRedirect
      },
      {
        path: "/packages",
        exact: true,
        element: HoldingPackages
      },
      {
        path: "/pickups",
        exact: true,
        element: HoldingPickups
      },
      {
        path: "/companies",
        exact: true,
        element: HoldingCompaniesList
      },
      {
        path: "/companies/new",
        exact: true,
        element: HoldingCompanyForm
      },
      {
        path: "/companies/:id",
        exact: true,
        element: HoldingCompanyForm
      },
      {
        path: "/inform",
        exact: true,
        element: HoldingInform
      },
      {
        path: "/my-account",
        exact: true,
        element: MyAcountClient
      },
      {
        path: "*",
        exact: true,
        element: Error
      }
    ]
  },
  {
    // Carril aislado del display de sala "Centro de Control Logístico IA".
    // Layout kiosko full-screen (sin sidebar/header). Solo lectura, scoped al
    // logisticCompanyId del usuario de display logueado.
    path: "/control-tower/*",
    element: ControlTowerLayout,
    exact: false,
    controlTowerOnly: true,
    children: [
      {
        path: "/",
        exact: true,
        element: ControlTowerIA,
      },
      {
        path: "*",
        exact: true,
        element: ControlTowerIA,
      },
    ],
  },
  {
    path: "/login/*",
    element: LoginLayout,
    exact: false,
    children: [
      {
        path: "/",
        exact: true,
        element: LoginView,
      },
      {
        path: "/recovery",
        exact: true,
        element: ForgotPassword,
      },
      {
        path: "/recovery/code",
        exact: true,
        element: EnterRecoveryCode,
      },
      {
        path: "/recovery/:token",
        exact: true,
        element: SetNewPassword,
      },
      {
        path: "/accept-invite/:token",
        exact: true,
        element: AcceptInvite,
      },
    ],
  },
  {
    path: "*",
    element: LoginLayout,
    exact: false,
    children: [
      {
        path: "*",
        exact: true,
        element: Error,
      },
    ],
  },
]

export default routes;